import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const apiUrl = process.env.REACT_APP_API_URL;

// Helper function to get auth headers
const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
});

// Async thunks
export const fetchBanners = createAsyncThunk(
  "banners/fetchBanners",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${apiUrl}/Banners/All/`, {
        headers: getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch banners");
    }
  }
);

export const fetchBannerDetails = createAsyncThunk(
  "banners/fetchBannerDetails",
  async (bannerId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${apiUrl}/Banners/${bannerId}/`, {
        headers: getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch banner details");
    }
  }
);

export const createBanner = createAsyncThunk(
  "banners/createBanner",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${apiUrl}/Banners/Create/`, formData, {
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to create banner");
    }
  }
);

export const updateBanner = createAsyncThunk(
  "banners/updateBanner",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await axios.put(
        `${apiUrl}/Banners/${id}/Update/`,
        data,
        {
          headers: {
            ...getAuthHeaders(),
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to update banner");
    }
  }
);

export const deleteBanner = createAsyncThunk(
  "banners/deleteBanner",
  async (bannerId, { rejectWithValue }) => {
    try {
      await axios.delete(`${apiUrl}/Banners/${bannerId}/Delete/`, {
        headers: getAuthHeaders(),
      });
      return bannerId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to delete banner");
    }
  }
);

export const toggleBannerStatus = createAsyncThunk(
  "banners/toggleStatus",
  async ({ id, is_active }, { rejectWithValue }) => {
    try {
      const response = await axios.patch(
        `${apiUrl}/Banners/${id}/Toggle/`,
        { is_active },
        {
          headers: getAuthHeaders(),
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to toggle banner status");
    }
  }
);

// Initial state
const initialState = {
  data: [],
  selectedBanner: null,
  isLoading: false,
  isLoadingDetails: false,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  isTogglingStatus: false,
  error: null,
  detailsError: null,
  createError: null,
  updateError: null,
  deleteError: null,
  toggleError: null,
  totalPages: 1,
  currentPage: 1,
  totalItems: 0,
};

// Slice
const bannerSlice = createSlice({
  name: "banners",
  initialState,
  reducers: {
    clearSelectedBanner: (state) => {
      state.selectedBanner = null;
      state.detailsError = null;
    },
    clearErrors: (state) => {
      state.error = null;
      state.detailsError = null;
      state.createError = null;
      state.updateError = null;
      state.deleteError = null;
      state.toggleError = null;
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all banners
      .addCase(fetchBanners.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBanners.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload.data || action.payload;
        if (action.payload.meta) {
          state.totalPages = action.payload.meta.totalPages;
          state.currentPage = action.payload.meta.currentPage;
          state.totalItems = action.payload.meta.totalItems;
        }
      })
      .addCase(fetchBanners.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Fetch banner details
      .addCase(fetchBannerDetails.pending, (state) => {
        state.isLoadingDetails = true;
        state.detailsError = null;
      })
      .addCase(fetchBannerDetails.fulfilled, (state, action) => {
        state.isLoadingDetails = false;
        state.selectedBanner = action.payload;
      })
      .addCase(fetchBannerDetails.rejected, (state, action) => {
        state.isLoadingDetails = false;
        state.detailsError = action.payload;
      })

      // Create banner
      .addCase(createBanner.pending, (state) => {
        state.isCreating = true;
        state.createError = null;
      })
      .addCase(createBanner.fulfilled, (state, action) => {
        state.isCreating = false;
        state.data.unshift(action.payload);
        state.totalItems += 1;
      })
      .addCase(createBanner.rejected, (state, action) => {
        state.isCreating = false;
        state.createError = action.payload;
      })

      // Update banner
      .addCase(updateBanner.pending, (state) => {
        state.isUpdating = true;
        state.updateError = null;
      })
      .addCase(updateBanner.fulfilled, (state, action) => {
        state.isUpdating = false;
        state.data = state.data.map((banner) =>
          banner.id === action.payload.id ? action.payload : banner
        );
        if (state.selectedBanner?.id === action.payload.id) {
          state.selectedBanner = action.payload;
        }
      })
      .addCase(updateBanner.rejected, (state, action) => {
        state.isUpdating = false;
        state.updateError = action.payload;
      })

      // Delete banner
      .addCase(deleteBanner.pending, (state) => {
        state.isDeleting = true;
        state.deleteError = null;
      })
      .addCase(deleteBanner.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.data = state.data.filter((banner) => banner.id !== action.payload);
        if (state.selectedBanner?.id === action.payload) {
          state.selectedBanner = null;
        }
        state.totalItems -= 1;
      })
      .addCase(deleteBanner.rejected, (state, action) => {
        state.isDeleting = false;
        state.deleteError = action.payload;
      })

      // Toggle banner status
      .addCase(toggleBannerStatus.pending, (state) => {
        state.isTogglingStatus = true;
        state.toggleError = null;
      })
      .addCase(toggleBannerStatus.fulfilled, (state, action) => {
        state.isTogglingStatus = false;
        state.data = state.data.map((banner) =>
          banner.id === action.payload.id ? action.payload : banner
        );
        if (state.selectedBanner?.id === action.payload.id) {
          state.selectedBanner = action.payload;
        }
      })
      .addCase(toggleBannerStatus.rejected, (state, action) => {
        state.isTogglingStatus = false;
        state.toggleError = action.payload;
      });
  },
});

// Export actions
export const { clearSelectedBanner, clearErrors, setCurrentPage } = bannerSlice.actions;

// Export reducer
export default bannerSlice.reducer;